/**
 * B4 · ভয়েস — `/search/voice`
 * Frame: `Oprappo P0 Screens.dc.html`, "B4 · ভয়েস".
 *
 * Frame note: "The one dark screen in the flow — listening is a distinct mode, and
 * inverting the ground says so without a label. Live Bangla transcription so a
 * mishearing is caught before it becomes a wrong result."
 *
 * The transcript is what gets sent, and it goes through the same one door as typed text:
 * `শেষ` hands the string to `/search?q=`, which posts it to `POST /v1/search` and lets
 * the server decide what it was. There is no separate voice endpoint and no client-side
 * branch on the words.
 *
 * ── THERE IS A MICROPHONE NOW, AND BEFORE THIS THERE WAS NOT ────────────────
 * This screen used to render a constant — `আমার এসি নষ্ট, আজই কাউকে দরকার` — under the
 * word `শুনছি…`, with a seven-bar waveform whose amber peak was a fixed height. It heard
 * the same sentence from everybody, in silence, with the microphone switched off. Tapping
 * `শেষ` searched for somebody else's broken air conditioner.
 *
 * It now uses the Web Speech API (`SpeechRecognition` / `webkitSpeechRecognition`) with
 * `lang="bn-BD"`, `interimResults` on so the transcription is live as the frame promises,
 * and `continuous` off so a pause ends the utterance rather than the screen listening
 * forever.
 *
 * ── AND WHERE THERE IS NO MICROPHONE, IT SAYS SO BY SHOWING NOTHING ─────────
 * The API is absent in Firefox and in most in-app browsers, and it fails at `start()`
 * when the microphone permission is refused or no input device exists. In every one of
 * those cases this screen **does not say `শুনছি…`**, does not draw the waveform, and shows
 * no transcript: the word means "I am listening", and a screen that is not listening must
 * not say it. The control still works — `শেষ` goes to `/search`, where the same query can
 * be typed into the one door — so the way out is the drawn one rather than a dead end.
 *
 * What it CANNOT do is explain itself in Bangla, because no frame draws a sentence for
 * "voice is not available on this phone" and inventing one is what docs/01 forbids. The
 * string is listed in `docs/bangla-needed/home-search.md § 4`. Until somebody writes it
 * the screen is silent about the reason, which is a smaller failure than being wrong about
 * it: `states/boundary.tsx` takes the same position about undrawn states in as many words.
 *
 * ── THE WAVEFORM IS DRAWN, NOT MEASURED ─────────────────────────────────────
 * The seven bars and their heights are transcribed from the frame and are `aria-hidden`
 * ornament. They are rendered only WHILE LISTENING, because a waveform on a screen with
 * no microphone open is a picture of sound that is not arriving. Driving them from real
 * amplitude would need an `AudioContext` analyser beside the recogniser, and it would
 * repaint several times a second — which the 2% fidelity gate screenshots. Reported, not
 * built: see `lib/relative-time.ts` for the same decision about the same gate.
 *
 * The frame paints its own border in its own ground colour
 * (`background:#0A2E37;border:1px solid #0A2E37`), which reads as artboard chrome rather
 * than UI. Transcribed as drawn and flagged in the report.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, IconButton } from '@oprappo/ui';
import { Frame } from '../onboarding/frame.js';
import { B4_COPY, B4_WAVEFORM } from './data.js';

/* ------------------------------------------------------- the browser API --- */

/**
 * The Web Speech API, typed to the three members this screen uses.
 *
 * `lib.dom` does not declare `SpeechRecognition` and has never declared the `webkit`
 * prefix, so the alternative to a local declaration is `any` at every call site. This is
 * narrower than the real interface on purpose: a member that is not declared here cannot
 * be reached by accident, and every browser that has the API at all has these.
 */
interface SpeechRecognitionAlternativeLike {
  transcript: string;
}
interface SpeechRecognitionResultLike {
  readonly length: number;
  isFinal: boolean;
  [index: number]: SpeechRecognitionAlternativeLike;
}
interface SpeechRecognitionResultListLike {
  readonly length: number;
  [index: number]: SpeechRecognitionResultLike;
}
interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: SpeechRecognitionResultListLike;
}
interface SpeechRecognitionLike {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  maxAlternatives: number;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

/**
 * The constructor, or null where the browser has none.
 *
 * Read at call time rather than at module load: a module-level constant is captured when
 * the bundle is parsed, which in a test environment is before any DOM exists, and the
 * screen would then believe for the life of the tab that there is no microphone.
 */
export function speechRecognitionCtor(): SpeechRecognitionCtor | null {
  const w = globalThis as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/**
 * The language the recogniser is asked for.
 *
 * `bn-BD`, not `bn-IN` and not the browser's default. Bangla as spoken in Bangladesh is
 * what this product is in (CLAUDE.md § bilingual convention), and a recogniser left on the
 * browser default transcribes Bangla speech into English words, which is a mishearing the
 * frame's own note says this screen exists to catch.
 */
export const VOICE_LANG = 'bn-BD';

/* ---------------------------------------------------------------- screen --- */

type VoicePhase =
  /** The API is absent, or `start()` was refused. Nothing is claimed on screen. */
  | 'unavailable'
  /** The recogniser is open. This is the only phase that may say `শুনছি…`. */
  | 'listening'
  /** It ended on its own — a pause, or `stop()`. The transcript stands. */
  | 'ended';

export function VoiceScreen(): JSX.Element {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<VoicePhase>('unavailable');
  const [transcript, setTranscript] = useState('');
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);

  useEffect(() => {
    const Ctor = speechRecognitionCtor();
    if (Ctor === null) return undefined;

    let live = true;
    let recognition: SpeechRecognitionLike;
    try {
      recognition = new Ctor();
    } catch {
      // A constructor that exists and throws is a browser that advertises the API and
      // does not have it. Same outcome as not having it: say nothing.
      return undefined;
    }
    recognition.lang = VOICE_LANG;
    // Live transcription is the frame's own promise — "so a mishearing is caught before
    // it becomes a wrong result" — and it is only live with interim results on.
    recognition.interimResults = true;
    // One utterance. `continuous` would keep the microphone open after the person stops
    // talking, which is a recording device left running on a screen they thought had
    // finished.
    recognition.continuous = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event: SpeechRecognitionEventLike): void => {
      if (!live) return;
      // The whole utterance, rebuilt from every result the recogniser has produced —
      // interim ones included. Appending only the newest is what makes a transcript that
      // loses its first half when the recogniser revises an earlier phrase.
      let text = '';
      for (let i = 0; i < event.results.length; i++) {
        const alternative = event.results[i]?.[0];
        if (alternative !== undefined) text += alternative.transcript;
      }
      setTranscript(text);
    };
    recognition.onerror = (): void => {
      if (!live) return;
      // No permission, no device, no network for the recogniser. The screen stops
      // claiming to listen; it does not explain why, because no sentence for that is
      // drawn (see the file header).
      setPhase('unavailable');
    };
    recognition.onend = (): void => {
      if (!live) return;
      setPhase((p) => (p === 'listening' ? 'ended' : p));
    };

    try {
      recognition.start();
      recognitionRef.current = recognition;
      setPhase('listening');
    } catch {
      setPhase('unavailable');
      return undefined;
    }

    return () => {
      live = false;
      recognitionRef.current = null;
      // `abort` rather than `stop`: leaving this screen must close the microphone at
      // once, not deliver one more result to a component that is gone.
      try {
        recognition.abort();
      } catch {
        /* a recogniser that is already closed is the state we wanted */
      }
    };
  }, []);

  const listening = phase === 'listening';
  const heard = transcript.trim();

  const finish = (): void => {
    recognitionRef.current?.stop();
    // Nothing heard is not an error and not a dead end: B2 is where the same query is
    // typed, and it is the same door. An empty `?q=` renders B2, so this is one navigate.
    navigate(heard === '' ? '/search' : `/search?q=${encodeURIComponent(heard)}`);
  };

  return (
    <Frame className="bg-teal-dark border border-teal-dark p-[20px]">
      <div className="flex justify-end">
        <IconButton control="close" size={22} stroke="#8FB0AE" onClick={() => navigate(-1)} />
      </div>

      <div className="flex-1 flex flex-col items-center justify-center gap-[26px]">
        <div className="w-[96px] h-[96px] border-2 border-amber rounded-full flex items-center justify-center">
          <Icon name="voice" size={34} stroke="#E8A33D" />
        </div>

        {/* Seven bars, `width:4px` each, `gap:5px`, in a 34px row. The peak is the amber.
            Drawn ornament, and drawn only while the microphone is actually open. */}
        {listening ? (
          <div className="flex gap-[5px] items-center h-[34px]" aria-hidden>
            {B4_WAVEFORM.map((bar, i) => (
              <div
                key={String(i)}
                className={`w-[4px] ${bar.heightClass} ${bar.amber ? 'bg-amber' : 'bg-on-teal-muted'}`}
              />
            ))}
          </div>
        ) : null}

        <div className="text-center">
          {listening ? (
            <div className="font-anek font-normal text-[13px] leading-[1.75] text-on-teal-muted tracking-[.04em]">
              {B4_COPY.listening_bn}
            </div>
          ) : null}
          {/*
            22px is at DISPLAY_BAND_PX, so the leading is 1.25 — as drawn, and as
            docs/02 § line height requires at and above 22px. The drawn frame breaks the
            sentence over two lines with an explicit `<br>`; a real transcript is any
            length, so it wraps on its own and the break is not transcribed.
          */}
          {heard === '' ? null : (
            <div
              className="font-anek font-medium text-[22px] leading-[1.25] text-white mt-[14px]"
              aria-live="polite"
            >
              {heard}
            </div>
          )}
        </div>
      </div>

      {/*
        A white 52px bar with teal label. `Button` has four tones — primary, secondary,
        tertiary, danger — and none of them is a white fill for a dark ground, so this is
        transcribed here and the missing tone is reported.
      */}
      <button
        type="button"
        onClick={finish}
        className="bg-white h-[52px] w-full flex items-center justify-center font-anek font-semibold text-[16px] leading-[1.75] text-teal border-0 rounded-none"
      >
        {B4_COPY.done_bn}
      </button>
    </Frame>
  );
}
